/**
 * ME View Service
 * Handles API calls for the productivity / ME View (SpreadSheetProd)
 * Endpoints migrated from legacy SpreadSheetService.js
 */

import { apiService } from './api';
import type { TeamMember, MetricsData } from '@/components/MEView/types';

/** Full request payload that matches the API contract */
export interface MeViewRequest {
  DashboardId: number;
  PageNumber: number;
  PageSize: number;
  DashboardViewTypeId: 11;
  Project: string;
  SearchText: string;
  Filters: string;
  LayersToHide: string;
  CustomFields: unknown[];
  Me?: number;
}

/** Raw API shape for a team member returned by GetViewMeGeneralInfo */
interface TeamMemberRaw {
  PersonId: number;
  Name: string;
  ImageName: string;
  Progress: number;
  CommittedSPs: number;
  CompletedSPs: number;
  AssignedTasks: number;
  CompletedAssignedTasks: number;
  WorkingCapacity: number;
  LoggedHours: number;
  Estimated: number;
  Remaining: number;
  ReworkByTask: number;
  DaysLeft: number;
  TimeEfficiency: number;
  ActualCapacity: number;
  TotalSprintCapacity: number;
  DailyCapacity: number;
  DaysOfSprint: number;
  TenantId: number;
}

/** Raw API shape for the MeView metric block inside GetViewMeDetailInfo */
interface MeViewRaw {
  CommittedSPs: number;
  CompletedSPs: number;
  Progress: number;
  AssignedTasks: number;
  CompletedAssignedTasks: number;
  WorkingCapacity: number;
  ActualCapacity: number;
  TotalSprintCapacity: number;
  LoggedHours: number;
  Estimated: number;
  Remaining: number;
  ReworkByTask: number;
  DaysLeft: number;
  TimeEfficiency: number;
}

export interface MeViewDetailResponse {
  Conversations: unknown[];
  MeView: MeViewRaw | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const computeTimePerSP = (workingCapacity: number, completedSPs: number): number | 'N/A' =>
  completedSPs === 0 ? 'N/A' : parseFloat((workingCapacity / completedSPs).toFixed(0));

const computeTimePerTask = (
  workingCapacity: number,
  completedAssignedTasks: number,
): number | 'N/A' =>
  completedAssignedTasks === 0
    ? 'N/A'
    : parseFloat((workingCapacity / completedAssignedTasks).toFixed(0));

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Fetches the team members summary (admin / PTO Approver view).
 * Returns TeamMember[] with computed TimePerSP / TimePerTask.
 * Supports cancellation via the returned { promise, cancel } pair.
 */
export const getViewMeGeneralInfo = (
  request: MeViewRequest,
): { promise: Promise<TeamMember[]>; cancel: () => void } =>
{
  let cancelled = false;

  const promise = apiService
    .post<TeamMemberRaw[]>('Conversation/GetViewMeGeneralInfo', request)
    .then(data =>
    {
      if (cancelled) return [];
      return (data ?? []).map(m => ({
        PersonId: m.PersonId,
        Name: m.Name,
        ImageName: m.ImageName,
        Progress: m.Progress,
        CommittedSPs: m.CommittedSPs,
        CompletedSPs: m.CompletedSPs,
        AssignedTasks: m.AssignedTasks,
        CompletedAssignedTasks: m.CompletedAssignedTasks,
        WorkingCapacity: m.WorkingCapacity,
        LoggedHours: m.LoggedHours,
        Estimated: m.Estimated,
        Remaining: m.Remaining,
        ReworkByTask: m.ReworkByTask,
        DaysLeft: m.DaysLeft,
        TimeEfficiency: m.TimeEfficiency,
        ActualCapacity: m.ActualCapacity,
        TotalSprintCapacity: m.TotalSprintCapacity,
        TimePerSP: computeTimePerSP(m.WorkingCapacity, m.CompletedSPs),
        TimePerTask: computeTimePerTask(m.WorkingCapacity, m.CompletedAssignedTasks),
      }));
    });

  return {
    promise,
    cancel: () =>
    {
      cancelled = true;
    },
  };
};

/**
 * Fetches individual member detail data (conversations + MeView metrics).
 */
export const getViewMeDetailInfo = (request: MeViewRequest): Promise<MeViewDetailResponse> =>
  apiService.post<MeViewDetailResponse>('Conversation/GetViewMeDetailInfo', request);

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/**
 * Maps the raw MeView API block to the MetricsData shape used by the component.
 */
export const mapMeViewToMetrics = (
  raw: MeViewRaw,
  startDate: string,
  dueDate: string,
): MetricsData => ({
  committedSPs: raw.CommittedSPs,
  completedSPs: raw.CompletedSPs,
  progress: raw.Progress,
  assignedTasks: raw.AssignedTasks,
  completedAssignedTasks: raw.CompletedAssignedTasks,
  workingCapacity: raw.WorkingCapacity,
  actualCapacity: raw.ActualCapacity,
  totalSprintCapacity: raw.TotalSprintCapacity,
  loggedHours: raw.LoggedHours,
  estimated: raw.Estimated,
  remaining: raw.Remaining,
  reworkbytask: raw.ReworkByTask,
  daysleft: raw.DaysLeft,
  timeEfficiency: raw.TimeEfficiency,
  timePerSP: computeTimePerSP(raw.WorkingCapacity, raw.CompletedSPs),
  timePerTask: computeTimePerTask(raw.WorkingCapacity, raw.CompletedAssignedTasks),
  numEmployees: 1,
  startDate,
  dueDate,
});

export const EMPTY_ME_METRICS: MetricsData = {
  committedSPs: 0,
  completedSPs: 0,
  progress: 0,
  assignedTasks: 0,
  completedAssignedTasks: 0,
  workingCapacity: 0,
  loggedHours: 0,
  estimated: 0,
  remaining: 0,
  reworkbytask: 0,
  daysleft: 0,
  timeEfficiency: 0,
  timePerSP: 'N/A',
  timePerTask: 'N/A',
  numEmployees: 0,
  dueDate: '',
  startDate: '',
  actualCapacity: 0,
  totalSprintCapacity: 0,
};
